import { defineComponent as _defineComponent } from 'vue'
import { trackEvent } from '@@/bits/analytics'
import FilterIconWithIndicator from 'library/v4/components/FilterIconWithIndicator.vue'
import OzDivider, { OzDividerColorScheme, OzDividerOrientation } from 'library/v4/components/OzDivider.vue'
import OzIcon from 'library/v4/components/OzIcon.vue'
import OzInput, { OzInputSizePreset } from 'library/v4/components/OzInput.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from 'library/v4/components/OzPlainButton.vue'
import SearchIconWithIndicator from 'library/v4/components/SearchIconWithIndicator.vue'
import { debounce } from 'lodash-es'
import { storeToRefs } from 'pinia'
import { useDarkModeStore } from 'pinia/dark_mode'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfaceFilterStore } from 'pinia/surface_filter'
import { useSurfacePostSearchStore } from 'pinia/surface_post_search'
import { useWindowSizeStore } from 'pinia/window_size'
import { computed, ref, watch } from 'vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfaceSearchBar',
  props: {
    shouldAnimateOnAppear: { type: Boolean, default: false }
  },
  setup(__props: any) {



const surfaceFilterStore = useSurfaceFilterStore()
const surfacePostSearchStore = useSurfacePostSearchStore()

const { isSmallerThanTabletLandscape } = storeToRefs(useWindowSizeStore())
const { backgroundLuminance, colorScheme, isMap, xSearchBar, isSearchBarInEditMode } = storeToRefs(useSurfaceStore())
const { xFilterModal, hasSelectedFilters } = storeToRefs(surfaceFilterStore)
const { searchTerm } = storeToRefs(surfacePostSearchStore)
const { isDarkMode: browserDarkMode } = storeToRefs(useDarkModeStore())

const isDarkMode = computed(() => {
  if (isSmallerThanTabletLandscape.value) return browserDarkMode.value
  return isMap.value ? colorScheme.value === 'dark' : backgroundLuminance.value === 'dark'
})

const shouldDisplayInEditMode = computed(
  () => isSearchBarInEditMode.value || isSmallerThanTabletLandscape.value || xFilterModal.value,
)

// Use a local ref that's updated on every keystroke to avoid the delay in showing
// the clear button (which is shown only when `hasSearchTerm` is true).
const localSearchTerm = ref(searchTerm.value)

const hasSearchTerm = computed(() => localSearchTerm.value.length > 0)

const clearSearchTermAndFocusInput = () => {
  localSearchTerm.value = ''
  inputRef.value?.focusInput()
}

const handleSearchInputEscKey = (): void => {
  // If there is a search term, we only want to clear it.
  // If no more search term, we want to reset.
  if (localSearchTerm.value.length > 0) {
    localSearchTerm.value = ''
    return
  }
  surfaceFilterStore.resetPostFilters()
  inputRef.value?.blurInput()
}

// We still want to delay showing the search results so we debounce the sync here.
const debouncedSyncSearchTerm = debounce(() => {
  surfacePostSearchStore.setSearchTerm(localSearchTerm.value.trim())
}, 300)

watch(localSearchTerm, debouncedSyncSearchTerm)
watch(searchTerm, (newSearchTerm) => (localSearchTerm.value = newSearchTerm.trim()))

const openFilterModal = (e: MouseEvent | KeyboardEvent) => {
  xFilterModal.value = true
  trackEvent('Surface', 'Opened filter posts panel')
}

const applySearchAndFilters = () => {
  xSearchBar.value = false
}

const searchBarRef = ref<HTMLDivElement>()

const exitEditModeIfFocusTargetIsOutside = (e: FocusEvent) => {
  // When OzInput loses focus, we don't want to set `isSearchBarInEditMode` to `false` too early
  // because it may be a Tab press to focus on the next buttons. To prevent this, we
  // check `e.relatedTarget` to see if it's an element inside the search bar or not.
  // If it's not, then we can safely set `isSearchBarInEditMode` to `false`.
  if (!searchBarRef.value) return
  if (!searchBarRef.value.contains(e.relatedTarget as Node | null)) {
    isSearchBarInEditMode.value = false
  }
}

const exitEditMode = () => {
  isSearchBarInEditMode.value = false
  inputRef?.value?.blurInput()
}

const inputRef = ref<OzInput>()

const selectInput = () => {
  inputRef.value?.selectInput()
}

const focusInput = () => {
  inputRef.value?.focusInput()
}

return { __sfc: true,surfaceFilterStore, surfacePostSearchStore, isSmallerThanTabletLandscape, backgroundLuminance, colorScheme, isMap, xSearchBar, isSearchBarInEditMode, xFilterModal, hasSelectedFilters, searchTerm, browserDarkMode, isDarkMode, shouldDisplayInEditMode, localSearchTerm, hasSearchTerm, clearSearchTermAndFocusInput, handleSearchInputEscKey, debouncedSyncSearchTerm, openFilterModal, applySearchAndFilters, searchBarRef, exitEditModeIfFocusTargetIsOutside, exitEditMode, inputRef, selectInput, focusInput, FilterIconWithIndicator, OzDivider, OzDividerColorScheme, OzDividerOrientation, OzIcon, OzInput, OzInputSizePreset, OzPlainButton, OzPlainButtonColorScheme, OzPlainButtonSizePreset, SearchIconWithIndicator }
}

})